import React from "react";
import { ListOrdered } from "lucide-react";
import { Button } from "../ui/Button";
import type { SunbedWaitingListItem } from "../../services/sunbedWaitingList";

interface SunbedWaitingListTableProps {
  items: SunbedWaitingListItem[];
  loading: boolean;
  indexOfFirstItem: number;
  onCancel: (id: number) => void;
  onStart: (item: SunbedWaitingListItem) => void;
  actionLoadingId: number | null;
}

export const SunbedWaitingListTable: React.FC<SunbedWaitingListTableProps> = ({
  items,
  loading,
  indexOfFirstItem,
  onCancel,
  onStart,
  actionLoadingId,
}) => {
  return (
    <div className="flex-1 overflow-y-auto relative">
      <table className="w-full">
        <thead className="sticky top-0 z-10 bg-[#885ABB] border-b border-gray-100">
          <tr className="grid grid-cols-12 gap-4 text-sm font-bold text-white tracking-wide px-6 py-4">
            <th className="col-span-1 text-left">#</th>
            <th className="col-span-3 text-left">Customer</th>
            <th className="col-span-2 text-left">Package</th>
            <th className="col-span-1 text-left">Mins</th>
            <th className="col-span-2 text-left">Waiting</th>
            <th className="col-span-1 text-left">Room</th>
            <th className="col-span-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="w-full divide-y divide-gray-100 bg-white">
          {loading ? (
            <tr className="h-64 w-full">
              <td colSpan={12} className="h-full">
                <div className="flex h-full w-full items-center justify-center">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#885ABB] border-t-transparent" />
                </div>
              </td>
            </tr>
          ) : items.length === 0 ? (
            <tr className="flex flex-col items-center justify-center h-64 text-gray-400 w-full">
              <td className="w-full h-full flex flex-col items-center justify-center">
                <ListOrdered className="w-12 h-12 mb-3 opacity-20" />
                <p className="text-lg font-medium text-gray-500">No one on the waiting list</p>
                <p className="text-sm text-center max-w-sm px-4">
                  Customers appear here when all beds are busy and they are added to the queue.
                </p>
              </td>
            </tr>
          ) : (
            items.map((item, idx) => (
              <tr
                key={item.id}
                className="grid grid-cols-12 gap-4 items-center px-6 py-4 hover:bg-purple-50/30 transition-colors bg-white text-sm w-full"
              >
                <td className="col-span-1 font-semibold text-[#885ABB]">
                  {item.queue_position || indexOfFirstItem + idx + 1}
                </td>
                <td className="col-span-3">
                  <p className="font-semibold text-gray-900">{item.customer}</p>
                  {item.customer_phone && (
                    <p className="text-xs text-gray-500 mt-0.5">{item.customer_phone}</p>
                  )}
                </td>
                <td className="col-span-2 text-gray-700">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span>{item.package_name}</span>
                    <span
                      className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                        item.source === "membership"
                          ? "bg-indigo-100 text-indigo-700"
                          : "bg-emerald-100 text-emerald-700"
                      }`}
                    >
                      {item.source === "membership" ? "Membership" : "Package"}
                    </span>
                  </div>
                </td>
                <td className="col-span-1 text-gray-700 tabular-nums">
                  {item.duration_minutes}
                  {item.delay_minutes > 0 && (
                    <span className="text-xs text-gray-400"> +{item.delay_minutes}d</span>
                  )}
                </td>
                <td className="col-span-2 text-gray-600">
                  {item.waited_formatted ||
                    (item.created_at
                      ? new Date(item.created_at).toLocaleString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "—")}
                </td>
                <td className="col-span-1 text-gray-600 text-xs">
                  {item.preferred_room_label || "—"}
                </td>
                <td className="col-span-2 flex justify-end gap-2">
                  <Button
                    type="button"
                    size="sm"
                    className="bg-[#885ABB] hover:bg-[#7748A4] text-white h-8 px-3 text-xs"
                    disabled={actionLoadingId === item.id}
                    onClick={() => onStart(item)}
                  >
                    Start
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 px-3 text-xs border-red-200 text-red-600 hover:bg-red-50"
                    disabled={actionLoadingId === item.id}
                    onClick={() => onCancel(item.id)}
                  >
                    Cancel
                  </Button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};
